// @flow

import get from 'lodash/get';

/**
 * Shop the Look variant priority list
 * @type {Array<string>}
 */
export const VARIANT_PRIORITY_LIST = ['colors', 'pattern', 'material', 'size'];

/**
 * Check whether pin has pin tags
 * @param {Object} pin - The Pin object itself
 * @return {boolean}  True if the pin has pin tags
 */
export const isSTLPin = (pin: Object) =>
  !!pin && (pin.shopping_flags || []).includes(PCONST.thrift.PinImpressionRichType.SHOP_THE_LOOK);

/**
 * Tries to extract the product merchant name from the Pin object
 * since products are currently represented in many ways.
 * @param {Object} pin - The Pin object itself
 * @return {string}  The name of the product merchant for this Pin
 */
export const getProductMerchant = (pin: Object) => {
  const linkDomainName = get(pin, 'link_domain.official_user.full_name');
  if (linkDomainName) {
    return linkDomainName;
  }
  const richSummarySiteName = get(pin, 'rich_summary.site_name');
  if (richSummarySiteName) {
    return richSummarySiteName;
  }
  if (pin.domain) {
    if (pin.domain === 'Uploaded by user') {
      const originalUploader = pin.is_repin ? pin.origin_pinner : pin.pinner;
      return (originalUploader || {}).full_name;
    }
    return pin.domain;
  }
  const viaPinnerName = get(pin, 'via_pinner.full_name');
  if (pin.is_repin && viaPinnerName) {
    return viaPinnerName;
  }
  return undefined;
};

/**
 * Get commerce richType
 * @param {Object} pin - The Pin object itself
 * @return {?Array<number>} Closeup rich types
 */
export const getRichTypes = (pin: Object) => {
  if (pin) {
    const types = pin.shopping_flags || [];

    if (
      !types.includes(PCONST.thrift.PinImpressionRichType.ORGANIC_PRODUCT_CAROUSEL) &&
      pin.product_pin_data &&
      pin.product_pin_data.items &&
      pin.product_pin_data.items[0] &&
      pin.product_pin_data.items[0].additional_images
    ) {
      types.push(PCONST.thrift.PinImpressionRichType.ORGANIC_PRODUCT_CAROUSEL);
    }

    return types;
  }
  return [];
};

/**
 * Get commerce auxData
 * @param {Object} pin - The Pin object itself
 * @param {Object} contextLogData - Additional commerce data for the aux data
 * @return {?Array<number>} Closeup rich types
 */
export const getCommerceAuxData = (pin: Object, contextLogData: Object = {}) => {
  if (pin) {
    const types = getRichTypes(pin);
    const commerceData = {
      ...contextLogData.commerce_data,
    };

    if (types.includes(PCONST.thrift.PinImpressionRichType.SHOP_THE_LOOK)) {
      commerceData.pin_is_shop_the_look = true;
    }
    if (types.includes(PCONST.thrift.PinImpressionRichType.AVAILABLE)) {
      commerceData.is_available = true;
    }
    if (types.includes(PCONST.thrift.PinImpressionRichType.PRODUCT_PIN_V2)) {
      commerceData.is_product_pin_v2 = true;
    }
    if (types.includes(PCONST.thrift.PinImpressionRichType.RICH_PRODUCT_PIN)) {
      commerceData.is_rich_product_pin = true;
    }
    if (types.includes(PCONST.thrift.PinImpressionRichType.ORGANIC_PRODUCT_CAROUSEL)) {
      commerceData.is_organic_product_carousel = true;
    }

    const stringifiedCommerceData = JSON.stringify(commerceData);

    if (stringifiedCommerceData !== '{}') {
      return {
        commerce_data: stringifiedCommerceData,
      };
    }
  }

  return null;
};
